import { Box, BoxProps } from '@mui/material';
import { useEffect, useState } from 'react';
import { theme } from 'styles/theme';

export type AnimatedStackedBarDataItem = {
  value: number;
  color: string | undefined;
  renderLabel?: (value: number) => React.ReactNode;
};

const exampleData: AnimatedStackedBarDataItem[] = [
  { value: 70, color: '#7cb77c' }, // green
  { value: 20, color: '#7c9bd7' }, // blue
  { value: 10, color: '#d77c7c' }, // red
];

const BAR_ANIMATION_DURATION = 1000; // ms, matches transition duration
const LABEL_FADE_DURATION = 300; // ms

export type AnimatedStackedBarData = {
  items: AnimatedStackedBarDataItem[];
};

export type AnimatedStackedBarProps = {
  data?: AnimatedStackedBarData;
  onlyRoundedCorners?: boolean;
  barBorderRadius?: number;
  componentsProps?: {
    container?: BoxProps;
    bar?: BoxProps;
  };
  renderOverlayContent?: () => React.ReactNode;
};

export const AnimatedStackedBar = ({
  data = {
    items: exampleData,
  },
  onlyRoundedCorners = false,
  barBorderRadius = 100,
  componentsProps,
  renderOverlayContent,
  ...props
}: AnimatedStackedBarProps) => {
  const [animatedWidths, setAnimatedWidths] = useState<number[]>([]);
  const [showLabels, setShowLabels] = useState(false);
  const { items: _dataItems } = data;
  const dataItems = _dataItems.filter((item) => item.value > 0);

  const total = dataItems.reduce((sum, item) => sum + item.value, 0);
  const percentages = dataItems.map((item) => (item.value / total) * 100);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setAnimatedWidths(percentages);
    }, 100); // delay to trigger CSS transition

    // Show labels after bar animation completes
    const labelTimeout = setTimeout(() => {
      setShowLabels(true);
    }, 100 + BAR_ANIMATION_DURATION);

    return () => {
      clearTimeout(timeout);
      clearTimeout(labelTimeout);
    };
  }, []);

  return (
    <Box
      {...componentsProps?.container}
      sx={{
        display: 'flex',
        height: 10,
        overflow: 'hidden',
        width: '100%',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: theme.spacing(1),
        position: 'relative',
        zIndex: 1,
        ...(total === 0
          ? {
              bgcolor: theme.colors?.utility[300],
              borderRadius: theme.spacing(2),
              '&:hover': {
                bgcolor: theme.colors?.utility[400],
              },
            }
          : {}),
        ...componentsProps?.container?.sx,
      }}
    >
      {dataItems.map((item, index) => {
        const isFirst = index === 0;
        const isLast = index === dataItems.length - 1;
        const width = animatedWidths[index] || 0;
        return (
          <Box
            key={index}
            {...componentsProps?.bar}
            sx={{
              width: `${width}%`,
              height: '100%',
              backgroundColor: item.color,
              transition: `width ${BAR_ANIMATION_DURATION}ms ease-in-out`,
              marginRight: index < dataItems.length - 1 ? '2px' : 0,
              display: 'flex',
              alignItems: 'center',
              px: theme.spacing(4),
              py: theme.spacing(2),
              borderTopLeftRadius: onlyRoundedCorners
                ? isFirst
                  ? barBorderRadius
                  : 0
                : barBorderRadius,
              borderBottomLeftRadius: onlyRoundedCorners
                ? isFirst
                  ? barBorderRadius
                  : 0
                : barBorderRadius,
              borderTopRightRadius: onlyRoundedCorners
                ? isLast
                  ? barBorderRadius
                  : 0
                : barBorderRadius,
              borderBottomRightRadius: onlyRoundedCorners
                ? isLast
                  ? barBorderRadius
                  : 0
                : barBorderRadius,
              ...componentsProps?.bar?.sx,
            }}
          >
            {item.renderLabel && (
              <Box
                sx={{
                  opacity: showLabels ? 1 : 0,
                  transition: `opacity ${LABEL_FADE_DURATION}ms ease-in-out`,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {item.renderLabel(item.value)}
              </Box>
            )}
          </Box>
        );
      })}
      {renderOverlayContent && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            zIndex: 2,
          }}
        >
          {renderOverlayContent()}
        </Box>
      )}
    </Box>
  );
};
